import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, ScanLine, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Pill } from "@/components/common";
import { api, errorMessage } from "@/lib/api";
import { compressImage, detectType } from "@/lib/upload";
import { cn } from "@/lib/utils";

const LABELS = { plate: "Immatriculation (A)", vin: "VIN / châssis (E)", brand: "Marque (D.1)", model: "Modèle (D.3)", version: "Version (D.2)", year: "Année — 1re immat. (B)", fuel: "Énergie (P.3)", engine: "Motorisation (P.1 / P.2)", color: "Couleur (R)" };
const toBase64 = (file) => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result).split(",")[1]); r.onerror = reject; r.readAsDataURL(file); });
const tone = (c) => (c >= 0.85 ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500" : c >= 0.5 ? "border-amber-500/40 bg-amber-500/10 text-amber-500" : "border-red-500/40 bg-red-500/10 text-red-500");

export function RegistrationScanner({ current = {}, vehicleId, onApply, className, testId = "ocr" }) {
  const camRef = useRef(null);
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const scan = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const img = await compressImage(file, 2000, 0.9);
      const type = detectType(img);
      if (!["image/jpeg", "image/png", "image/webp"].includes(type)) throw new Error("Utilisez une photo JPG, PNG ou WEBP de la carte grise");
      setPreview(URL.createObjectURL(img));
      const { data } = await api.post("/ai/ocr-registration", { image_base64: await toBase64(img), content_type: type, vehicle_id: vehicleId || null });
      setResult(data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const onPick = (e) => {
    scan(e.target.files?.[0]);
    e.target.value = "";
  };

  return (
    <>
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid={`${testId}-camera-input`} />
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`${testId}-file-input`} />
      <div className={cn("flex flex-wrap items-center gap-2", className)} data-testid={testId}>
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => camRef.current?.click()} className="rounded-full border-primary/50 text-primary hover:bg-primary/10" data-testid={`${testId}-scan-button`}>
          {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ScanLine className="mr-1 h-4 w-4" />} {busy ? "Lecture en cours…" : "Scanner la carte grise"}
        </Button>
        <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`${testId}-file-button`}><Upload className="mr-1 h-4 w-4" /> Depuis une photo</Button>
        <span className="text-xs text-muted-foreground">Pré-remplit plaque, VIN, marque, modèle… après votre vérification.</span>
      </div>
      {result && <VerifyDialog result={result} preview={preview} current={current} onClose={() => setResult(null)} onApply={(fields) => { onApply(fields, result.file_id); setResult(null); }} testId={testId} />}
    </>
  );
}

function VerifyDialog({ result, preview, current, onClose, onApply, testId }) {
  const [rows, setRows] = useState(() => Object.keys(LABELS).map((key) => {
    const f = result.fields?.[key] || {};
    const value = f.value === null || f.value === undefined ? "" : String(f.value);
    return { key, value, confidence: f.confidence ?? 0, apply: !!value && (f.confidence ?? 0) >= 0.5 };
  }));
  const set = (i, patch) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const selected = rows.filter((r) => r.apply && r.value.trim());
  const apply = () => onApply(Object.fromEntries(selected.map((r) => [r.key, r.key === "year" ? Number(r.value) || "" : r.value.trim()])));
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl" aria-describedby={undefined} data-testid={`${testId}-verify-dialog`}>
        <DialogHeader><DialogTitle>Vérification de la lecture <span className="text-sm font-normal text-muted-foreground">— rien n'est enregistré sans votre validation</span></DialogTitle></DialogHeader>
        {!result.document_detected && <div className="flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-500" data-testid={`${testId}-not-detected`}><AlertTriangle className="h-4 w-4" /> Le document ne ressemble pas à un certificat d'immatriculation. Reprenez la photo du recto, à plat et bien éclairé.</div>}
        {result.warnings?.length > 0 && (
          <ul className="space-y-1 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-500" data-testid={`${testId}-warnings`}>
            {result.warnings.map((w, i) => <li key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {w}</li>)}
          </ul>
        )}
        {result.existing_vehicle && <div className="rounded-xl border border-blue-500/40 bg-blue-500/10 px-3 py-2 text-sm" data-testid={`${testId}-existing`}>Véhicule déjà connu : <Link to={`/vehicules/${result.existing_vehicle.id}`} className="font-semibold text-primary underline-offset-4 hover:underline">{result.existing_vehicle.plate} · {result.existing_vehicle.label}</Link> ({result.existing_vehicle.client_name || "client inconnu"}). Évitez de créer un doublon.</div>}
        <div className="grid gap-4 md:grid-cols-[240px_1fr]">
          {preview && <img src={preview} alt="Carte grise scannée" className="max-h-[380px] w-full rounded-xl border border-border object-contain" data-testid={`${testId}-preview`} />}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left"><th className="p-2 eyebrow">Appliquer</th><th className="p-2 eyebrow">Champ</th><th className="p-2 eyebrow">Valeur lue (modifiable)</th><th className="p-2 eyebrow">Confiance</th><th className="p-2 eyebrow">Fiche actuelle</th></tr></thead>
              <tbody>
                {rows.map((r, i) => {
                  const cur = current?.[r.key] ? String(current[r.key]) : "";
                  return (
                    <tr key={r.key} className="border-b border-border/50 last:border-0" data-testid={`${testId}-row-${r.key}`}>
                      <td className="p-2"><Checkbox checked={r.apply} onCheckedChange={(v) => set(i, { apply: !!v })} data-testid={`${testId}-apply-${r.key}`} /></td>
                      <td className="p-2 font-medium">{LABELS[r.key]}</td>
                      <td className="p-2"><Input value={r.value} onChange={(e) => set(i, { value: e.target.value })} className={cn("h-9 min-w-[160px]", ["plate", "vin"].includes(r.key) && "font-mono")} data-testid={`${testId}-value-${r.key}`} /></td>
                      <td className="p-2">{r.value ? <Pill className={tone(r.confidence)} testId={`${testId}-confidence-${r.key}`}>{Math.round(r.confidence * 100)} %</Pill> : <span className="text-xs text-muted-foreground">non lu</span>}</td>
                      <td className={cn("p-2 text-xs", cur && cur !== r.value ? "font-medium text-amber-500" : "text-muted-foreground")}>{cur || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Vert ≥ 85 % · orange ≥ 50 % · rouge : relisez sur le document. Les champs décochés ne sont pas modifiés. La photo est archivée dans « Documents & photos » du véhicule comme certificat d'immatriculation.</p>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid={`${testId}-cancel`}>Annuler</Button>
          <Button onClick={apply} disabled={!selected.length} data-testid={`${testId}-apply-button`}>Appliquer {selected.length} champ{selected.length > 1 ? "s" : ""} au formulaire</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
